import { useEffect, useState } from 'react';
import { Download, Printer, Send, Copy, Check, Edit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Invoice } from '../../types';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { emailService } from '../../services/emailService';
import { SEO } from '../shared/SEO';
import toast from 'react-hot-toast';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
// Import preview components (to be created)
import { ClassicInvoicePreview } from './previews/ClassicInvoicePreview';
import { MinimalInvoicePreview } from './previews/MinimalInvoicePreview';
import { BoldInvoicePreview } from './previews/BoldInvoicePreview';
import { ElegantInvoicePreview } from './previews/ElegantInvoicePreview';
import { useNavigate } from 'react-router-dom';
import { ModernInvoicePreview } from './previews/ModernInvoicePreview';

interface InvoiceTemplateProps {
  invoice: Invoice;
  showActions?: boolean;
}

export function InvoiceTemplate({ invoice, showActions = true }: InvoiceTemplateProps) {
  const { currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Check if this is a print request
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('print') === 'true') {
      setTimeout(() => {
        window.print();
      }, 1000);
    }
  }, []);

  if (!currentWorkspace) return null;

  const company = currentWorkspace.settings?.branding;
  if (!company || !company.companyName || !company.address || !company.email) {
    toast.error('Please complete your business info in workspace settings.');
    return null;
  }

  console.log('[InvoiceTemplate] Current workspace:', {
    name: currentWorkspace.name,
    hasLogo: !!currentWorkspace.logo,
    logoUrl: currentWorkspace.logo,
    settings: currentWorkspace.settings
  });

  console.log('[InvoiceTemplate] Company branding:', {
    ...company,
    hasLogo: !!company.logo
  });
  
  // Use invoice currency if present, otherwise workspace or USD
  const currency = invoice.currency || currentWorkspace.settings?.currency || 'USD';



  const downloadPDF = async () => {
    setIsDownloading(true);
    try {
      // Create a modified workspace object with the logo and currency from company branding and invoice
      const workspaceWithLogoAndCurrency = {
        ...currentWorkspace,
        logo: company.logo,
        settings: {
          ...currentWorkspace.settings,
          currency: currency
        }
      };
      const template = invoice.template || 'default';
      await generateInvoicePDF(invoice, workspaceWithLogoAndCurrency, template);
    } catch (error) {
      console.error('Error handling PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const printInvoice = async () => {
    try {
      // Wait for all images to load
      const images = Array.from(document.querySelectorAll('#invoice-template img')) as HTMLImageElement[];
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve(true);
              img.onload = () => resolve(true);
              img.onerror = () => resolve(false);
            })
        )
      );
      
      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 500));
      
      window.print();
    } catch (error) {
      console.error('Error printing:', error);
      toast.error('Error printing. Please try again.');
    }
  };

  const sendInvoiceEmail = async () => {
    if (!currentWorkspace || !invoice.client.email) return;
    
    // Check if email service is configured
    if (!currentWorkspace.settings?.resendApiKey) {
      alert('Please configure your Resend API key in workspace settings before sending emails.');
      return;
    }
    
    setIsSending(true);
    try {
      const success = await emailService.sendInvoiceEmail(invoice, currentWorkspace);
      if (success) {
        setEmailSent(true);
        toast.success('Invoice email sent successfully!');
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        alert('Failed to send email. Please check your email configuration in workspace settings.');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send email. Please check your email configuration and try again.');
    } finally {
      setIsSending(false);
    }
  };

  const copyFirebaseDownloadUrl = async () => {
    if (!currentWorkspace || !invoice) return;
    try {
      const safeClientName = invoice.client?.name
        ?.toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '') || 'unnamed_client';
      const fileName = `invoices/${currentWorkspace.name}/${safeClientName}/Invoice_${invoice.invoiceNumber}.pdf`;
      const storageRef = ref(storage, fileName);
      const downloadUrl = await getDownloadURL(storageRef);
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
      toast.success('Download link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy download link. Make sure the invoice PDF has been generated.');
    }
  };

  const handleEditPDF = () => {
    navigate(`/app/invoices/${invoice.id}/edit`);
  };

  const template = invoice.template || 'default';
  let PreviewComponent;
  switch (template) {
    case 'modern':
      PreviewComponent = ModernInvoicePreview;
      break;
    case 'minimal':
      PreviewComponent = MinimalInvoicePreview;
      break;
    case 'bold':
      PreviewComponent = BoldInvoicePreview;
      break;
    case 'elegant':
      PreviewComponent = ElegantInvoicePreview;
      break;
    case 'default':
    default:
      PreviewComponent = ClassicInvoicePreview;
  }

  return (
    <>
      <SEO
        title={`Invoice #${invoice.invoiceNumber} - ${company.companyName}`}
        description={`View invoice #${invoice.invoiceNumber} for ${invoice.client.name} from ${company.companyName}.`}
        keywords={["invoice", invoice.invoiceNumber, company.companyName, invoice.client.name, "FreelancePay"]}
        type="article"
      />
      {/* Action Bar Header */}
      {showActions && (
        <div className="flex gap-2 px-6 py-3 bg-white shadow-lg rounded-xl border mb-8 print:hidden" style={{ minHeight: '64px' }}>
          <button
            onClick={downloadPDF}
            disabled={isDownloading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </button>
          <button
            onClick={handleEditPDF}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Edit PDF
          </button>
          <button
            onClick={printInvoice}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 transition-colors"
          >
            <Printer className="h-4 w-4" />
            Print
          </button>
          <button
            onClick={sendInvoiceEmail}
            disabled={isSending}
            className={`px-4 py-2 ${
              emailSent 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50`}
          >
            {emailSent ? (
              <>
                <Check className="h-4 w-4" />
                Sent!
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {isSending ? 'Sending...' : 'Send Email'}
              </>
            )}
          </button>
          {invoice && (
            <button
              onClick={copyFirebaseDownloadUrl}
              className={`px-4 py-2 ${
                copied 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-600 hover:bg-gray-700'
              } text-white rounded-lg flex items-center gap-2 transition-colors`}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy Download Link
                </>
              )}
            </button>
          )}
        </div>
      )}
      {/* Invoice Template Content */}
      <PreviewComponent invoice={invoice} company={company} workspace={currentWorkspace} />
    </>
  );
}